import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bot, Plus, Sparkles, Settings, Crown, LogOut,
  FolderOpen, Calendar, BarChart3, Users, Zap, Rocket, History, LayoutDashboard, List,
  Sun, Moon
} from "lucide-react";
import PlanSelectionModal from "@/components/PlanSelectionModal";
import { useTheme } from "@/context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function UserAvatar({ user, size = 8 }) {
  const [imgError, setImgError] = useState(false);
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : user?.email?.[0]?.toUpperCase() || "U";

  if (user?.picture && !imgError) {
    return (
      <img
        src={user.picture}
        alt={user.name || "User"}
        onError={() => setImgError(true)}
        className={`w-${size} h-${size} rounded-full object-cover ring-2 ring-slate-200 hover:ring-[#00b37a]/50 transition-all`}
      />
    );
  }

  return (
    <div
      className={`w-${size} h-${size} rounded-full flex items-center justify-center text-sm font-bold ring-2 ring-slate-200 hover:ring-[#00b37a]/50 transition-all`}
      style={{ background: "linear-gradient(135deg, #00b37a, #D946EF)", color: "#fff" }}
    >
      {initials}
    </div>
  );
}

const NAV_TABS = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, match: ["/dashboard"] },
  { label: "All Tests", path: "/dashboard", icon: List, match: [] },
  { label: "Analytics", path: "/analytics", icon: BarChart3, match: ["/analytics"] },
  { label: "Suites", path: "/suites", icon: FolderOpen, match: ["/suites"] },
  { label: "Schedules", path: "/schedules", icon: Calendar, match: ["/schedules"] },
  { label: "History", path: "/history", icon: History, match: ["/history"] },
  { label: "Settings", path: "/settings", icon: Settings, match: ["/settings"] },
];

export function AppNav({ className }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [planStatus, setPlanStatus] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const planName = planStatus?.plan || user?.plan || "free";
  const isFree = !planStatus?.is_paid;
  const planLabel = planName.charAt(0).toUpperCase() + planName.slice(1);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/billing/status`, { withCredentials: true })
      .then(res => setPlanStatus(res.data))
      .catch(() => {});
  }, [user]);

  const isPaidPlan = planStatus?.is_paid;
  const genLeft = planStatus && !isPaidPlan ? planStatus.generations_remaining : null;
  const runLeft = planStatus && !isPaidPlan ? planStatus.executions_remaining : null;
  const showCredits = genLeft !== null && genLeft !== undefined;
  const bothZero = showCredits && genLeft === 0 && runLeft === 0;

  const minLeft = showCredits ? Math.min(genLeft ?? 0, runLeft ?? 0) : null;
  const creditStyle = !showCredits ? null
    : minLeft >= 3 ? { bg: "bg-[#00b37a]/10 border-[#00b37a]/30", text: "text-[#00b37a]" }
    : minLeft >= 1 ? { bg: "bg-yellow-50 border-yellow-300", text: "text-yellow-600" }
    : { bg: "bg-orange-50 border-orange-300", text: "text-orange-500" };

  const handleUpgrade = () => setShowUpgrade(true);

  const isTabActive = (tab) => {
    if (tab.match.length === 0) return false;
    return tab.match.some(m => location.pathname === m || location.pathname.startsWith(m + "/"));
  };

  return (
    <TooltipProvider delayDuration={200}>
      <header className={`app-nav-bar border-b ${isDark ? "bg-[#0b0f1a] border-white/[0.06]" : "bg-white border-[#e2e6ed]"} ${className || ""}`}>
        {/* Top bar */}
        <div className="px-6 md:px-10 h-14 flex items-center justify-between max-w-screen-xl mx-auto">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer select-none"
            onClick={() => navigate("/dashboard")}
          >
            <div className="w-8 h-8 bg-[#00b37a] rounded-md flex items-center justify-center shadow-sm">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span
              className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}
              style={{ fontFamily: "Unbounded, sans-serif" }}
            >
              AutoQA.ai
            </span>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleTheme}
                  className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${isDark ? "text-slate-400 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}
                  aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-slate-800 text-white border-slate-700 text-xs">
                {isDark ? "Light mode" : "Dark mode"}
              </TooltipContent>
            </Tooltip>

            {/* Generate from Story */}
            <Button
              data-testid="nav-generate-from-story-btn"
              variant="outline"
              className="border-slate-300 text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-sm rounded-md h-8 px-3"
              onClick={() => navigate("/generate-from-story")}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-[#a855f7]" />
              Generate from story
            </Button>

            {/* New Test */}
            <Button
              data-testid="nav-create-test-btn"
              className="bg-[#00b37a] text-white hover:bg-[#009966] text-sm rounded-md h-8 px-3 font-semibold shadow-sm"
              onClick={() => navigate("/create-test")}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Test
            </Button>

            {/* Divider */}
            {user && <div className="w-px h-6 bg-slate-200 mx-1" />}

            {user && (
              <div className="flex items-center gap-1.5">
                {/* Admin badge */}
                {user.role === "super_admin" && (
                  <Button
                    data-testid="nav-admin-btn"
                    variant="ghost"
                    size="sm"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md h-8 px-2 text-xs"
                    onClick={() => navigate("/admin")}
                  >
                    <Crown className="w-3.5 h-3.5 mr-1" />
                    Admin
                  </Button>
                )}

                {/* Plan badge */}
                <span
                  className="hidden sm:flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border"
                  style={
                    isFree
                      ? { background: "#f1f5f9", color: "#64748b", borderColor: "#e2e8f0" }
                      : { background: "#f0fdf4", color: "#00b37a", borderColor: "#bbf7d0" }
                  }
                >
                  {!isFree && <Crown className="w-3 h-3" />}
                  {planLabel}
                </span>

                {/* Credits pill */}
                {showCredits && creditStyle && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border ${creditStyle.bg} ${creditStyle.text}`}>
                        <Sparkles className="w-3 h-3" />
                        {genLeft}
                        <span className="opacity-60">gen</span>
                        <span className="opacity-40">·</span>
                        <Zap className="w-3 h-3" />
                        {runLeft}
                        <span className="opacity-60">run</span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-800 text-white border-slate-700 text-xs">
                      {bothZero
                        ? "Free monthly credits used up — upgrade for unlimited"
                        : `${genLeft} generations · ${runLeft} runs left this month`}
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Upgrade */}
                {isFree && (
                  <Button
                    size="sm"
                    onClick={handleUpgrade}
                    className="rounded-full px-3 h-7 text-xs font-semibold bg-[#00b37a]/10 text-[#00b37a] border border-[#00b37a]/30 hover:bg-[#00b37a]/20 transition-all"
                  >
                    <Rocket className="w-3 h-3 mr-1" />
                    Upgrade
                  </Button>
                )}

                {/* Teams */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="nav-teams-btn"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md h-8 w-8 p-0"
                      onClick={() => navigate("/teams")}
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-slate-800 text-white border-slate-700 text-xs">Teams</TooltipContent>
                </Tooltip>

                {/* Avatar dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="outline-none cursor-pointer ml-1">
                      <UserAvatar user={user} size={8} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-60 bg-white border border-slate-200 text-slate-900 shadow-lg rounded-xl"
                  >
                    <DropdownMenuLabel className="p-0">
                      <div className="flex items-center gap-3 px-3 py-3">
                        <UserAvatar user={user} size={9} />
                        <div className="flex flex-col min-w-0">
                          <span className="font-semibold text-sm truncate text-slate-900">{user.name || "User"}</span>
                          <span className="text-xs text-slate-500 truncate">{user.email}</span>
                          <Badge
                            className="mt-1 w-fit text-[10px] px-2 py-0 rounded-full"
                            style={
                              isFree
                                ? { background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }
                                : { background: "#f0fdf4", color: "#00b37a", border: "1px solid #bbf7d0" }
                            }
                          >
                            {isFree ? "Free Plan" : `${planLabel} Plan`}
                          </Badge>
                        </div>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator className="bg-slate-100" />

                    {isFree && (
                      <DropdownMenuItem
                        onClick={handleUpgrade}
                        className="cursor-pointer text-[#00b37a] hover:text-[#00b37a] hover:bg-[#00b37a]/5 mx-1 rounded-lg"
                      >
                        <Rocket className="w-4 h-4 mr-2" />
                        Upgrade Plan
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem
                      onClick={() => navigate("/settings")}
                      className="cursor-pointer text-slate-700 hover:text-slate-900 hover:bg-slate-50 mx-1 rounded-lg"
                      data-testid="nav-settings-btn"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="bg-slate-100" />

                    <DropdownMenuItem
                      onClick={logout}
                      className="cursor-pointer text-red-500 hover:text-red-600 hover:bg-red-50 mx-1 rounded-lg"
                      data-testid="nav-logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>

        {/* Tab row */}
        <div className={`px-6 md:px-10 border-t max-w-screen-xl mx-auto ${isDark ? "border-white/[0.06]" : "border-[#e2e6ed]"}`}>
          <nav className="flex items-end gap-0 -mb-px">
            {NAV_TABS.map((tab) => {
              const active = isTabActive(tab);
              return (
                <button
                  key={tab.label}
                  data-testid={`nav-tab-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => navigate(tab.path)}
                  className={`app-nav-tab flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? "border-[#00b37a] text-[#00b37a]"
                      : `border-transparent ${isDark ? "text-slate-400 hover:text-slate-100 hover:border-slate-500" : "text-slate-500 hover:text-slate-800 hover:border-slate-300"}`
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}

            {/* API Tests tab */}
            <button
              data-testid="nav-tab-api-tests"
              onClick={() => navigate("/create-api-test")}
              className={`app-nav-tab flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                location.pathname.includes("api") && location.pathname !== "/analytics"
                  ? "border-[#00b37a] text-[#00b37a]"
                  : `border-transparent ${isDark ? "text-slate-400 hover:text-slate-100 hover:border-slate-500" : "text-slate-500 hover:text-slate-800 hover:border-slate-300"}`
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              API Tests
            </button>
          </nav>
        </div>
      </header>

      <PlanSelectionModal
        open={showUpgrade}
        dismissible
        showFree={false}
        onClose={() => setShowUpgrade(false)}
      />
    </TooltipProvider>
  );
}
